import { Control, FieldValues } from "react-hook-form";
import { Dispatch, FC, SetStateAction, useEffect, useState } from "react";
import SearchTable from "../../../components/template/Search/SearchTable";
import { Option } from "../../../types/General/Option";
import useFetchData from "../../../hooks/data/useFetchData";
import { getPartners } from "../../../services/TabelasAuxiliares/getPartner";
import { getIndicatorLevel } from "../../../services/Indicator/getIndicatorLevel";
import { getCollectionFrequency } from "../../../services/Indicator/getCollectionFrequency";
import { getIndicatorState } from "../../../services/Indicator/getIndicatorState";
import { updateSelectedFilterItemsName } from "../../../helpers/updateSelectedFilterItemsName";
import SelectGroup from "../../../components/molecule/SelectGroup/SelectGroup";
import { indicatorManagementTableSelectFilterData } from "../data/Indicator/indicatorManagementTableSelectFilterData";
import { TableConfig } from "../../../types/Components/Table/TableConfig";
import { FilterConfig } from "../../../types/Components/Table/FilterConfig";
import { PaginationConfig } from "../../../types/Components/Table/PaginationConfig";
import { getFiltersArray } from "../../../helpers/getFiltersArray";
import { ModalConfig } from "../../../types/Components/Modal/ModalConfig";
import ModalIndicatorDetailsOnly from "../../Indicator/component/ModalIndicatorDetailsOnly";
import { getIndicator } from "../../../services/Indicator/getIndicator";
import { AuthUser } from "../../../types/User/AuthUser";

interface IndicatorManagementSearchTableProps {
  user: AuthUser;
  setIsSearching: Dispatch<SetStateAction<boolean>>;
  control: Control<FieldValues, any>;
  filters: Record<string, string | undefined>;
  filterNames: string[];
  setFilter: (filterName: string, value: string | undefined) => void;
  tableConfig: TableConfig;
  paginationConfig: PaginationConfig;
  modalConfig: ModalConfig;
}

const IndicatorManagementSearchTable: FC<
  IndicatorManagementSearchTableProps
> = ({
  user,
  setIsSearching,
  control,
  filters,
  filterNames,
  setFilter,
  tableConfig,
  paginationConfig,
  modalConfig,
}) => {
  const [selectedFilterItems, setSelectedFilterItems] = useState<string>("");
  const [filterState, setFilterState] = useState({
    IndicatorId: null,
    PartnerId:
      user?.role?.level !== undefined &&
      user?.role.level.id === 2 &&
      user?.partner
        ? { id: user.partner.id, name: user.partner.name }
        : null,
    LevelId: null,
    StateId: null,
    FrequencyId: null,
  });
  const [optionsState, setOptionsState] = useState({
    indicatorOptions: [] as Option[],
    partnerOptions: [] as Option[],
    levelOptions: [] as Option[],
    stateOptions: [] as Option[],
    frequencyOptions: [] as Option[],
  });

  const { isLoaded: partnerLoaded } = useFetchData(getPartners, (data) =>
    setOptionsState((prev) => ({ ...prev, partnerOptions: data })),
  );
  const { isLoaded: levelLoaded } = useFetchData(getIndicatorLevel, (data) =>
    setOptionsState((prev) => ({ ...prev, levelOptions: data })),
  );
  const { isLoaded: stateLoaded } = useFetchData(getIndicatorState, (data) =>
    setOptionsState((prev) => ({ ...prev, stateOptions: data })),
  );
  const { isLoaded: frequencyLoaded } = useFetchData(
    getCollectionFrequency,
    (data) => setOptionsState((prev) => ({ ...prev, frequencyOptions: data })),
  );
  const { isLoaded: indicatorLoaded } = useFetchData(getIndicator, (data) =>
    setOptionsState((prev) => ({ ...prev, indicatorOptions: data })),
  );

  useEffect(() => {
    updateSelectedFilterItemsName(
      filterState,
      filters,
      setSelectedFilterItems,
      filterNames,
    );
  }, [filters, filterState, filterNames]);

  const filtersArray = getFiltersArray(filterNames, filters, setFilter);

  const setSelectedFilter = (filterName: string, value: string | null) => {
    setFilterState((prev) => ({ ...prev, [filterName]: value }));
  };

  const selectComponents = indicatorManagementTableSelectFilterData(
    optionsState.indicatorOptions,
    optionsState.partnerOptions,
    optionsState.levelOptions,
    optionsState.stateOptions,
    optionsState.frequencyOptions,
    control,
    setFilter,
    setSelectedFilter,
    indicatorLoaded,
    partnerLoaded,
    levelLoaded,
    stateLoaded,
    frequencyLoaded,
    user,
  );
  const filterChildren = (
    <SelectGroup selectComponents={selectComponents} width="230px" />
  );

  const filterConfig: FilterConfig = {
    filterChildren,
    filters: filtersArray,
    selectedFilterItem: selectedFilterItems,
    hasInputSearch: false,
  };

  return (
    <SearchTable
      searchTableName="Indicadores do sistema"
      showModal
      headingFontSize="2xl"
      accordionTitle="Filtrar"
      tableConfig={tableConfig}
      filterConfig={filterConfig}
      paginationConfig={paginationConfig}
      alertDateCountdownDescription="Geração automática do próximo boletim"
      setIsSearching={setIsSearching}
    >
      <ModalIndicatorDetailsOnly control={control} modalConfig={modalConfig} />
    </SearchTable>
  );
};

export default IndicatorManagementSearchTable;
